import { FormEvent, JSX, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Form, Modal } from "react-bootstrap";
import { toast } from "react-toastify";

import { createDashboardApi } from "src/api/dashboardApi";
import { toSnakeCase } from "src/helpers/Dashboard";
import { useAuthStore } from "src/store/authStore";
import type {
  DashboardListItem,
  DashboardListResponse,
} from "src/types/dashboard";
import { AxiosError } from "axios";

type CreateDashboardPayload = {
  code: string;
  name: string;
  user_types: string[];
};

type CreateDashboardModalProps = {
  show: boolean;
  initialName?: string;
  onClose: () => void;
  onSubmit: (dashboard: CreateDashboardPayload) => void;
};

export default function CreateDashboardModal({
  show,
  initialName = "",
  onClose,
  onSubmit,
}: CreateDashboardModalProps): JSX.Element {
  const [dashboardName, setDashboardName] = useState(initialName);
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null,
  );
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const { mutate: createDashboard, isPending: isCreatingDashboard } =
    useMutation({
      mutationFn: async (payload: CreateDashboardPayload) =>
        createDashboardApi(payload),
      onSuccess: async (_response, payload) => {
        queryClient.setQueryData<DashboardListResponse | undefined>(
          ["dashboards"],
          (currentDashboards) => {
            const nextDashboard: DashboardListItem = {
              code: payload.code,
              name: payload.name,
              is_default: false,
              user_types: payload.user_types,
              saved_filters: null,
            };

            if (!currentDashboards) {
              return {
                dashboards: [nextDashboard],
                total: 1,
              };
            }

            const dashboardAlreadyExists = currentDashboards.dashboards.some(
              (dashboard) => dashboard.code === payload.code,
            );

            if (dashboardAlreadyExists) {
              return currentDashboards;
            }

            return {
              ...currentDashboards,
              dashboards: [...currentDashboards.dashboards, nextDashboard],
              total: currentDashboards.total + 1,
            };
          },
        );
        await queryClient.invalidateQueries({
          queryKey: ["dashboards"],
        });
        onSubmit(payload);
      },
      onError: (
        error: AxiosError & { response: { data: { detail: string } } },
      ) => {
        toast.error(
          error?.response?.data?.detail || "Failed to create dashboard.",
        );
      },
    });

  useEffect(() => {
    if (show) {
      setDashboardName(initialName);
      setValidationMessage(null);
    }
  }, [initialName, show]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedDashboardName = dashboardName.trim();

    if (!trimmedDashboardName) {
      setValidationMessage("Dashboard name is required.");
      return;
    }

    if (trimmedDashboardName.length < 3) {
      setValidationMessage(
        "Dashboard name must be at least 3 characters long.",
      );
      return;
    }

    const createDashboardPayload = {
      code: toSnakeCase(trimmedDashboardName) ?? "",
      name: trimmedDashboardName,
      user_types: [user?.ref_type || "Manufacturer"],
    };

    createDashboard(createDashboardPayload);
  };

  return (
    <Modal show={show} centered onHide={onClose}>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Dashboard</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="new-dashboard-name">
            <Form.Label className="float-start">Dashboard name</Form.Label>
            <Form.Control
              type="text"
              value={dashboardName}
              onChange={(event) => {
                setDashboardName(event.target.value);
                if (validationMessage) {
                  setValidationMessage(null);
                }
              }}
              isInvalid={validationMessage !== null}
              placeholder="Enter dashboard name"
              disabled={isCreatingDashboard}
              autoFocus
            />
            <Form.Control.Feedback type="invalid">
              {validationMessage}
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={onClose}
            disabled={isCreatingDashboard}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={isCreatingDashboard}
          >
            {isCreatingDashboard ? "Creating..." : "Continue"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
